import { useState } from 'react';
import { motion } from 'framer-motion';
import { infonuagiqueData } from '../data/infonuagiqueData';
import AikupCard from '../components/AikupCard';
import AikupModal from '../components/AikupModal';
import ExpertVisionModal from '../components/ExpertVisionModal';
import CloudPriveBZModal from '../components/CloudPriveBZModal';
import CollocationModal from '../components/CollocationModal';
import EnvironnementPartageModal from '../components/EnvironnementPartageModal';
import InfrastructureRedondanceModal from '../components/InfrastructureRedondanceModal';
import BackupArchiveModal from '../components/BackupArchiveModal';
import SecuriteAccesModal from '../components/SecuriteAccesModal';
import GestionLicencesSPLAModal from '../components/GestionLicencesSPLAModal';
import ServiceClientCloudModal from '../components/ServiceClientCloudModal';
import AIInsightBubble from '../components/AIInsightBubble';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import FormationGestionDocumentaireCard from '../components/FormationGestionDocumentaireCard';
import ArbrePRIsMECard from '../components/ArbrePRIsMECard';
import type { InfonuagiqueItem } from '../data/infonuagiqueData';

export default function Infonuagique() {
  const [selectedItem, setSelectedItem] = useState<InfonuagiqueItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expertVisionModal, setExpertVisionModal] = useState<{
    isOpen: boolean;
    expert: 'dany' | 'yannick' | 'francois' | null;
    item: InfonuagiqueItem | null;
  }>({
    isOpen: false,
    expert: null,
    item: null,
  });
  const [isCloudPriveOpen, setIsCloudPriveOpen] = useState(false);
  const [isCollocationOpen, setIsCollocationOpen] = useState(false);
  const [isEnvironnementPartageOpen, setIsEnvironnementPartageOpen] = useState(false);
  const [isInfrastructureOpen, setIsInfrastructureOpen] = useState(false);
  const [isBackupOpen, setIsBackupOpen] = useState(false);
  const [isSecuriteOpen, setIsSecuriteOpen] = useState(false);
  const [isSPLAOpen, setIsSPLAOpen] = useState(false);
  const [isServiceClientOpen, setIsServiceClientOpen] = useState(false);

  const handleCardClick = (item: InfonuagiqueItem) => {
    if (item.id === 1) {
      // Cloud Privé BZ
      setIsCloudPriveOpen(true);
    } else if (item.id === 2) {
      // Collocation
      setIsCollocationOpen(true);
    } else if (item.id === 3) {
      // Environnement Partagé
      setIsEnvironnementPartageOpen(true);
    } else if (item.id === 4) {
      // Infrastructure & Redondance
      setIsInfrastructureOpen(true);
    } else if (item.id === 5) {
      // Backup & Archive
      setIsBackupOpen(true);
    } else if (item.id === 6) {
      // Sécurité & Accès
      setIsSecuriteOpen(true);
    } else if (item.id === 7) {
      // Gestion Licences SPLA
      setIsSPLAOpen(true);
    } else if (item.id === 8) {
      // Service Client
      setIsServiceClientOpen(true);
    } else {
      setSelectedItem(item);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedItem(null), 300);
  };

  const leaders = [
    {
      name: "Richard Delisle",
      title: "Directeur Infonuagique - Protecteur du Cloud",
      avatar: "/RD.png",
      vision: "Richard est notre protecteur du cloud. Sa rigueur mémorable et son dévouement pour un département qui exige l'excellence pour éviter les coupures font de lui la personne faite pour ce travail. Tout est loggué, tout est contrôlé, tout est méthodique à croire qu'il est un robot. Malgré son paraitre un peu bougon, c'est un être d'exception qui adore aider et partager ses connaissances. C'est un être qui gagne à être connu. L'exemple parfait qu'un contenu incroyable donne une beauté extraordinaire. Il voit PRIsME comme l'outil qui structure et documente notre excellence cloud.",
      attentes: "Richard s'attend à ce que PRIsME reflète notre rigueur méthodique et notre excellence opérationnelle. Il veut voir chaque processus documenté, chaque contrôle tracé, chaque standard respecté. Son objectif : que PRIsME soit l'outil qui démontre notre professionnalisme et notre dévouement à l'excellence, tout en facilitant le partage de connaissances.",
      pagePRIsME: "La page PRIsME d'Infonuagique reflète notre excellence : cloud privé, collocation, environnement partagé, infrastructure redondante, backup & archive, sécurité, gestion licences SPLA. C'est notre vitrine d'excellence cloud qui rassure et inspire confiance."
    }
  ];

  // Organiser par catégorie
  const categories = Array.from(new Set(infonuagiqueData.map(item => item.category)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header 
        title="Infonuagique (Cloud)" 
        subtitle="Cloud Privé BZ Inc. - Infrastructure robuste, service humain exceptionnel, rapport qualité/prix incomparable"
      />
      
      <div className="max-w-7xl mx-auto px-8 pb-12">
        <LeadersSection leaders={leaders} title="Notre Leader - Infonuagique" />
        
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500"
          >
            <p className="text-sm text-gray-600 mb-1">Services Disponibles</p>
            <p className="text-3xl font-bold text-gray-900">{infonuagiqueData.length}</p>
            <p className="text-xs text-gray-500 mt-1">Solutions cloud</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
          >
            <p className="text-sm text-gray-600 mb-1">Score Maturité</p>
            <p className="text-3xl font-bold text-gray-900">
              {Math.round(infonuagiqueData.reduce((sum, item) => sum + (item.kpiScore || 0), 0) / infonuagiqueData.length)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Moyenne excellence</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500"
          >
            <p className="text-sm text-gray-600 mb-1">Catégories</p>
            <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500 mt-1">Domaines couverts</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-cyan-500"
          >
            <p className="text-sm text-gray-600 mb-1">Infrastructure</p>
            <p className="text-3xl font-bold text-gray-900">
              {infonuagiqueData.filter(item => item.category === 'Infrastructure' || item.category === 'Services').length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Services infrastructure</p>
          </motion.div>
        </div>

        {/* Cards par catégorie */}
        {categories.map((category, categoryIndex) => {
          const itemsInCategory = infonuagiqueData.filter(item => item.category === category);
          
          return (
            <div key={category} className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="flex-1 h-px bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                <h2 className="text-2xl font-bold text-gray-800 px-4">{category}</h2>
                <div className="flex-1 h-px bg-gradient-to-l from-blue-500 to-cyan-500"></div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itemsInCategory.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: categoryIndex * 0.1 + idx * 0.05 }}
                  >
                    <AikupCard
                      item={item}
                      onClick={() => handleCardClick(item)}
                      onExpertClick={(expert: 'dany' | 'yannick' | 'francois') => {
                        setExpertVisionModal({
                          isOpen: true,
                          expert,
                          item,
                        });
                      }}
                      index={idx}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Cards Formation et Arbre PRIsME */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <FormationGestionDocumentaireCard
            department="Infonuagique (Cloud)"
            domain="Infonuagique"
            index={0}
          />
          <ArbrePRIsMECard
            index={1}
          />
        </div>
      </div>

      <AikupModal
        item={selectedItem as any}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      <CloudPriveBZModal
        isOpen={isCloudPriveOpen}
        onClose={() => setIsCloudPriveOpen(false)}
      />

      <CollocationModal
        isOpen={isCollocationOpen}
        onClose={() => setIsCollocationOpen(false)}
      />

      <EnvironnementPartageModal
        isOpen={isEnvironnementPartageOpen}
        onClose={() => setIsEnvironnementPartageOpen(false)}
      />

      <InfrastructureRedondanceModal
        isOpen={isInfrastructureOpen}
        onClose={() => setIsInfrastructureOpen(false)}
      />

      <BackupArchiveModal
        isOpen={isBackupOpen}
        onClose={() => setIsBackupOpen(false)}
      />

      <SecuriteAccesModal
        isOpen={isSecuriteOpen}
        onClose={() => setIsSecuriteOpen(false)}
      />

      <GestionLicencesSPLAModal
        isOpen={isSPLAOpen}
        onClose={() => setIsSPLAOpen(false)}
      />

      <ServiceClientCloudModal
        isOpen={isServiceClientOpen}
        onClose={() => setIsServiceClientOpen(false)}
      />

      <ExpertVisionModal
        isOpen={expertVisionModal.isOpen}
        onClose={() => setExpertVisionModal({ isOpen: false, expert: null, item: null })}
        expert={expertVisionModal.expert}
        item={expertVisionModal.item}
      />

      <AIInsightBubble />
    </div>
  );
}

